'use client';

import { useTranslations } from 'next-intl';
import { ChevronDown } from 'lucide-react';

import { cn } from '../lib/cn';
import { Select, type SelectOption } from './Select';

type DaemonTriggerWorktreeSettingsState = {
  isExistingModeDisabled: boolean;
  worktreeSelectionMode: 'new' | 'existing';
  selectedExistingWorktreeId: string;
  selectedExistingRepositoryId: string;
  daemonRepositoryId: string;
  daemonBaseBranch: string;
  branchInputMode: 'dropdown' | 'manual';
  branchInputHelpMessage: string | null;
  showProjectRepositorySettingsCta: boolean;
  isRepositoryBranchesFetching: boolean;
  isActiveWorktreesFetching: boolean;
  isActiveWorktreesError: boolean;
};

type DaemonTriggerWorktreeSettingsOptions = {
  daemonRepositoryOptions: SelectOption[];
  daemonBranchOptions: SelectOption[];
  existingWorktreeOptions: SelectOption[];
  existingRepositoryOptions: SelectOption[];
};

type DaemonTriggerWorktreeSettingsActions = {
  onWorktreeSelectionModeChange: (value: 'new' | 'existing') => void;
  onExistingWorktreeChange: (value: string) => void;
  onExistingRepositoryChange: (value: string) => void;
  onDaemonRepositoryIdChange: (value: string) => void;
  onDaemonBaseBranchChange: (value: string) => void;
  onOpenProjectRepositorySettings: () => void;
};

type DaemonTriggerWorktreeSettingsProps = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  state: DaemonTriggerWorktreeSettingsState;
  options: DaemonTriggerWorktreeSettingsOptions;
  actions: DaemonTriggerWorktreeSettingsActions;
};

const labelOf = (options: SelectOption[], value: string) =>
  options.find((option) => option.value === value)?.label ?? '';

export const DaemonTriggerWorktreeSettings = ({
  isOpen,
  onOpenChange,
  state,
  options,
  actions,
}: DaemonTriggerWorktreeSettingsProps) => {
  const tLayout = useTranslations('components.appLayout');
  const summary =
    state.worktreeSelectionMode === 'existing'
      ? [
          labelOf(options.existingRepositoryOptions, state.selectedExistingRepositoryId),
          labelOf(options.existingWorktreeOptions, state.selectedExistingWorktreeId),
        ]
          .filter(Boolean)
          .join(' · ')
      : [
          labelOf(options.daemonRepositoryOptions, state.daemonRepositoryId),
          state.branchInputMode === 'manual'
            ? state.daemonBaseBranch
            : labelOf(options.daemonBranchOptions, state.daemonBaseBranch) || state.daemonBaseBranch,
        ]
          .filter(Boolean)
          .join(' · ');
  const hasExistingWorktreeOptions = options.existingWorktreeOptions.some((option) => option.value.length > 0);

  return (
    <div className="space-y-2">
      <button
        type="button"
        className="flex w-full items-center justify-between rounded-lg border border-base-300/70 bg-transparent px-3 py-2 text-xs font-medium text-[var(--color-fg-muted)] transition hover:bg-base-200/30"
        onClick={() => onOpenChange(!isOpen)}
        aria-expanded={isOpen}
      >
        <span className="flex min-w-0 flex-col gap-0.5 text-left">
          <span>{tLayout('runnerBoxSettings')}</span>
          {!isOpen && summary ? (
            <span className="truncate text-[11px] font-normal text-[var(--color-fg-subtle)]">{summary}</span>
          ) : null}
        </span>
        <ChevronDown
          className={cn('h-3.5 w-3.5 shrink-0 transition-transform', isOpen && 'rotate-180')}
          aria-hidden="true"
        />
      </button>
      <div className={cn('space-y-2', !isOpen && 'hidden')}>
        <div className="grid w-full grid-cols-2 gap-2">
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-base-300 px-3 py-2">
            <input
              type="radio"
              name="worktree-selection-mode"
              className="radio radio-xs radio-primary"
              checked={state.worktreeSelectionMode === 'new'}
              onChange={() => actions.onWorktreeSelectionModeChange('new')}
            />
            <span className="text-sm font-medium">{tLayout('daemonWorktreeModeNew')}</span>
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-base-300 px-3 py-2">
            <input
              type="radio"
              name="worktree-selection-mode"
              className="radio radio-xs radio-primary"
              checked={state.worktreeSelectionMode === 'existing'}
              onChange={() => actions.onWorktreeSelectionModeChange('existing')}
              disabled={state.isExistingModeDisabled}
            />
            <span className="text-sm font-medium">{tLayout('daemonWorktreeModeExisting')}</span>
          </label>
        </div>
        {/* 저장소를 고르면 해당 저장소의 박스만 남는다. 모바일에서는 선택값을 읽을 수 있도록 세로 배치한다. */}
        {state.worktreeSelectionMode === 'existing' ? (
          <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2">
            <div className="flex flex-col min-w-0">
              <label
                className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]"
                htmlFor="daemon-existing-repository-select"
              >
                {tLayout('daemonRepository')}
              </label>
              <Select
                id="daemon-existing-repository-select"
                size="sm"
                value={state.selectedExistingRepositoryId}
                onChange={actions.onExistingRepositoryChange}
                options={options.existingRepositoryOptions}
                disabled={state.isExistingModeDisabled}
                fullWidth
              />
            </div>
            <div className="flex flex-col min-w-0">
              <label
                className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]"
                htmlFor="daemon-existing-worktree-select"
              >
                {tLayout('daemonExistingWorktree')}
              </label>
              <Select
                id="daemon-existing-worktree-select"
                size="sm"
                value={state.selectedExistingWorktreeId}
                onChange={actions.onExistingWorktreeChange}
                options={options.existingWorktreeOptions}
                disabled={state.isExistingModeDisabled}
                fullWidth
              />
            </div>
          </div>
        ) : null}
        {state.worktreeSelectionMode === 'new' ? (
          <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2">
            <div className="flex flex-col min-w-0">
              <label
                className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]"
                htmlFor="daemon-repository-select"
              >
                {tLayout('daemonRepository')}
              </label>
              <Select
                id="daemon-repository-select"
                size="sm"
                value={state.daemonRepositoryId}
                onChange={actions.onDaemonRepositoryIdChange}
                options={options.daemonRepositoryOptions}
                fullWidth
              />
            </div>
            <div className="flex flex-col min-w-0">
              <label
                className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]"
                htmlFor={state.branchInputMode === 'manual' ? 'daemon-branch-input' : 'daemon-branch-select'}
              >
                {tLayout('daemonBranch')}
              </label>
              {state.branchInputMode === 'manual' ? (
                <input
                  id="daemon-branch-input"
                  type="text"
                  className="input input-bordered input-sm w-full"
                  value={state.daemonBaseBranch}
                  onChange={(event) => actions.onDaemonBaseBranchChange(event.target.value)}
                  disabled={state.daemonRepositoryId.length === 0}
                  placeholder={tLayout('daemonBranchManualPlaceholder')}
                />
              ) : (
                <Select
                  id="daemon-branch-select"
                  size="sm"
                  value={state.daemonBaseBranch}
                  onChange={actions.onDaemonBaseBranchChange}
                  options={options.daemonBranchOptions}
                  disabled={state.daemonRepositoryId.length === 0 || state.isRepositoryBranchesFetching}
                  fullWidth
                />
              )}
            </div>
          </div>
        ) : null}
        {state.worktreeSelectionMode === 'existing' && state.isActiveWorktreesError ? (
          <p className="text-xs text-warning">{tLayout('daemonExistingWorktreeUnavailable')}</p>
        ) : null}
        {state.worktreeSelectionMode === 'existing' &&
        !state.isActiveWorktreesFetching &&
        !hasExistingWorktreeOptions ? (
          <p className="text-xs text-warning">{tLayout('daemonExistingWorktreeEmpty')}</p>
        ) : null}
        {state.worktreeSelectionMode === 'new' &&
        state.daemonRepositoryId.length > 0 &&
        state.branchInputHelpMessage ? (
          <div className="flex flex-col items-stretch gap-2 rounded-lg bg-warning/10 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="min-w-0 flex-1 text-xs text-[var(--chip-warning-fg)]">{state.branchInputHelpMessage}</p>
            {state.showProjectRepositorySettingsCta ? (
              <button
                type="button"
                className="btn btn-ghost btn-xs shrink-0 self-end text-[var(--chip-warning-fg)] sm:self-auto"
                onClick={actions.onOpenProjectRepositorySettings}
              >
                {tLayout('daemonBranchOpenProjectRepositorySettings')}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export type {
  DaemonTriggerWorktreeSettingsActions,
  DaemonTriggerWorktreeSettingsOptions,
  DaemonTriggerWorktreeSettingsProps,
  DaemonTriggerWorktreeSettingsState,
};
